import type { TOC } from '@ember/component/template-only';
import { on } from '@ember/modifier';
import { PrismButton, PrismCard } from '@mylighthouse/prism-ember';
import { ICONS_LOGOS_MAP } from '@mylighthouse/prism-logo';
import { t } from 'ember-intl';
import { FocusScreen } from 'frontend-utils';
import HotelBenchmarkInsightCompetitorsCompsetBuilderCompsetBuilderHeader from 'lumen/components/hotel/benchmark-insight/competitors/compset-builder/compset-builder-header';
import HotelBenchmarkInsightCompetitorsCompsetBuilderStepperPanel from 'lumen/components/hotel/benchmark-insight/competitors/compset-builder/stepper-panel';
import HotelBenchmarkInsightCompetitorsTableCompetitors from 'lumen/components/hotel/benchmark-insight/competitors/table-competitors';
import HotelBenchmarkInsightDesiredCompsetInputSearchDesiredCompetitors from 'lumen/components/hotel/benchmark-insight/desired-compset/input-search-desired-competitors';
import type HotelSettingsCompetitorsBenchmarkCompsetBuilderDesiredCompsetController from 'lumen/controllers/hotel/settings/competitors/benchmark/compset/builder/desired-compset';

interface HotelSettingsCompetitorsBenchmarkCompsetBuilderDesiredCompsetSignature {
  Args: {
    controller: HotelSettingsCompetitorsBenchmarkCompsetBuilderDesiredCompsetController;
    model: unknown;
  };
}

<template>
  <FocusScreen
    @hasCancel={{true}}
    @isCancelDisabled={{false}}
    @logoHtml={{ICONS_LOGOS_MAP.logo-dark-with-text}}
    @onClose={{@controller.onClose}}
    @size="large"
  >
    <:header>
      <HotelBenchmarkInsightCompetitorsCompsetBuilderCompsetBuilderHeader
        @currentStep={{1}}
      />
    </:header>
    <:body>
      <div class="compset-builder__body">
        <div
          class="compset-builder__body__header bg-elevation-surface px-1000 pt-1000 pb-600"
        >
          <h1>{{t
              "settings.competitors.benchmark.compset.builder.desired-compset.title"
            }}</h1>
        </div>

        <div class="compset-builder__body__right-sidebar pr-1000 pl-500">
          <HotelBenchmarkInsightCompetitorsCompsetBuilderStepperPanel
            @description={{t
              "settings.competitors.benchmark.compset.builder.stepper.desired_compset.description"
              class="underline"
              htmlSafe=true
              link="https://help.mylighthouse.com/en/articles/6796449-competitor-rules-in-benchmark-insight"
            }}
            @steps={{@controller.stepperPanelSteps}}
            @title={{t
              "settings.competitors.benchmark.compset.builder.stepper.desired_compset.title"
            }}
          />
        </div>

        <div class="compset-builder__body__content pr-500 pb-1000 pl-1000">
          <div class="mb-400 flex flex-col">
            <HotelBenchmarkInsightDesiredCompsetInputSearchDesiredCompetitors
              @isDisabled={{@controller.benchmarkInsightCompetitorsDesiredCompset.isSearchInputDisabled}}
              @usageTrackingEventTrigger="compset-builder"
            />
          </div>

          <PrismCard
            @contentLayout="noPadding"
            @isSecondary={{true}}
            class="prism-card--with-table-competitors prism-card--with-border"
          >
            <:header as |Header|>
              <div class="text-200--regular flex flex-row items-center gap-200">
                <Header.title>
                  {{t
                    "settings.competitors.benchmark.compset.builder.desired-compset.selected_competitors"
                  }}
                </Header.title>

                {{@controller.formattedNumberOfCompetitors}}
              </div>
            </:header>

            <:content>
              <HotelBenchmarkInsightCompetitorsTableCompetitors
                {{! @glint-expect-error: Incorrect type }}
                @competitors={{@controller.benchmarkInsightCompetitorsDesiredCompset.desiredCompetitors}}
                @copyRICompset={{@controller.benchmarkInsightCompetitorsDesiredCompset.desiredCompset.copyRICompset}}
                @deleteCompetitor={{@controller.benchmarkInsightCompetitorsDesiredCompset.desiredCompset.deleteCompetitor}}
                @isLoading={{@controller.benchmarkInsightCompetitorsDesiredCompset.isLoading}}
                @isRowHoverDisabled={{true}}
                @trackDeleteEvent={{true}}
                @usageAnalyticsEventTrigger="compset-builder"
              />
            </:content>
          </PrismCard>
        </div>
      </div>
    </:body>
    <:footer>
      <div class="flex w-full flex-row justify-end gap-300">
        <div>
          <PrismButton
            @buttonType="brand"
            @iconAfter="arrow-right"
            @isDisabled={{@controller.isNextDisabled}}
            @isLoading={{@controller.benchmarkInsightCompetitorsDesiredCompset.saveDesiredCompsetTask.isRunning}}
            @label={{t "general.next"}}
            {{on "click" @controller.onClickSave}}
          />
        </div>
      </div>

    </:footer>
  </FocusScreen>
</template> satisfies TOC<HotelSettingsCompetitorsBenchmarkCompsetBuilderDesiredCompsetSignature>;
